/**
 * HIKARI Theme JavaScript
 * Handles: mobile nav, header dropdown, search overlay, scent filter,
 *           PDP variant selector, ingredient bars, scent finder quiz,
 *           FAQ accordion, cart AJAX updates, scroll-based nav, thumbnail gallery
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITY
  ============================================================ */

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.from((scope || document).querySelectorAll(selector));
  }

  function on(el, event, fn, options) {
    if (el) el.addEventListener(event, fn, options || false);
  }

  function off(el, event, fn) {
    if (el) el.removeEventListener(event, fn);
  }

  // Swap the PDP gallery image. sizes and srcset must land before src, or the browser
  // resolves the plain src against the previous image's candidate list.
  // The gallery is a scroll-snap track: the swapped image lives in a slide, and the
  // user may have flicked to a different one — scroll back so the swap is visible.
  function setMainImage(img, data) {
    if (!img || !data || !data.src) return;
    img.sizes = data.sizes || '';
    img.srcset = data.srcset || '';
    img.src = data.src;
    if (typeof data.alt === 'string') img.alt = data.alt;
    const slide = img.closest ? img.closest('.hikari-pdp__slide') : null;
    if (slide && slide.parentElement) {
      slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }
  }

  function trapFocus(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ============================================================
     SCROLL-BASED NAV TRANSPARENCY
  ============================================================ */

  function initScrollNav() {
    const header = qs('#hikari-header');
    if (!header) return;

    let ticking = false;

    function update() {
      if (window.scrollY > 60) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* ============================================================
     DESKTOP DROPDOWN NAV
  ============================================================ */

  function initDropdown() {
    const triggers = qsa('.hikari-nav__link--dropdown-trigger');

    triggers.forEach(function (trigger) {
      const dropdown = qs('#' + trigger.getAttribute('aria-controls'));
      if (!dropdown) return;

      function open() {
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.classList.add('is-open');
      }

      function close() {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
      }

      on(trigger, 'click', function (e) {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        isOpen ? close() : open();
      });

      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!trigger.contains(e.target) && !dropdown.contains(e.target)) {
          close();
        }
      });

      // Close on Escape
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
    });
  }

  /* ============================================================
     MOBILE NAV
  ============================================================ */

  function initMobileNav() {
    const toggle = qs('.hikari-mobile-toggle');
    const nav = qs('#mobile-nav');
    const backdrop = qs('#mobile-nav-backdrop');
    const close = qs('.hikari-mobile-nav__close');
    const hamburger = toggle && toggle.querySelector('.hamburger-icon');
    const closeIcon = toggle && toggle.querySelector('.close-icon');

    if (!toggle || !nav) return;

    function openNav() {
      nav.classList.add('is-open');
      nav.removeAttribute('aria-hidden');
      backdrop && backdrop.classList.add('is-visible');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (hamburger) hamburger.style.display = 'none';
      if (closeIcon) closeIcon.style.display = 'block';
      trapFocus(nav);
    }

    function closeNav() {
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      backdrop && backdrop.classList.remove('is-visible');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (hamburger) hamburger.style.display = 'block';
      if (closeIcon) closeIcon.style.display = 'none';
      toggle.focus();
    }

    on(toggle, 'click', openNav);
    on(close, 'click', closeNav);
    on(backdrop, 'click', closeNav);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) closeNav();
    });

    // Mobile accordion sub-menus
    qsa('.hikari-mobile-nav__accordion').forEach(function (btn) {
      on(btn, 'click', function () {
        const sub = btn.nextElementSibling;
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !isExpanded);
        if (sub) sub.hidden = isExpanded;
      });
    });
  }

  /* ============================================================
     SEARCH OVERLAY
  ============================================================ */

  function initSearch() {
    const searchToggle = qs('#search-toggle');
    const searchOverlay = qs('#search-overlay');
    const searchClose = qs('#search-close');

    if (!searchToggle || !searchOverlay) return;

    function openSearch() {
      searchOverlay.classList.add('is-open');
      searchOverlay.removeAttribute('aria-hidden');
      const input = searchOverlay.querySelector('input[type="search"]');
      if (input) setTimeout(function () { input.focus(); }, 80);
    }

    function closeSearch() {
      searchOverlay.classList.remove('is-open');
      searchOverlay.setAttribute('aria-hidden', 'true');
      searchToggle.focus();
    }

    on(searchToggle, 'click', openSearch);
    on(searchClose, 'click', closeSearch);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && searchOverlay.classList.contains('is-open')) closeSearch();
    });
  }

  /* ============================================================
     SCENT FILTER (Homepage + Collection)
  ============================================================ */

  function initScentFilter() {
    const tabs = qsa('.hikari-scent-nav__tab');
    const grid = qs('#home-product-grid') || qs('#collection-grid');
    const formatSelect = qs('#format-filter');

    if (!tabs.length || !grid) return;

    let activeScent = 'all';
    let activeFormat = 'all';

    function applyFilter() {
      const cards = qsa('.hikari-card', grid);
      cards.forEach(function (card) {
        const cardScent = card.dataset.scent || '';
        const cardFormat = card.dataset.format || '';
        const scentMatch = activeScent === 'all' || cardScent === activeScent;
        const formatMatch = activeFormat === 'all' || cardFormat === activeFormat;
        card.style.display = scentMatch && formatMatch ? '' : 'none';
      });
    }

    tabs.forEach(function (tab) {
      on(tab, 'click', function () {
        tabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
        activeScent = tab.dataset.scent || 'all';
        applyFilter();
      });
    });

    if (formatSelect) {
      on(formatSelect, 'change', function () {
        activeFormat = formatSelect.value;
        applyFilter();
      });
    }
  }

  /* ============================================================
     PDP: VARIANT SELECTOR + IMAGE GALLERY + SCENT COLOUR UPDATE
  ============================================================ */

  function initPDP() {
    const pdp = qs('.hikari-pdp');
    if (!pdp) return;

    const mainImg = qs('#pdp-main-img', pdp);
    const thumbs = qsa('.hikari-pdp__thumb', pdp);
    const optionBtns = qsa('.hikari-pdp__option-btn', pdp);
    const variantInput = qs('#pdp-variant-id', pdp);
    const priceEl = qs('.hikari-pdp__price', pdp);
    const comparePriceEl = qs('.hikari-pdp__compare-price', pdp);
    const atcBtn = qs('#pdp-atc', pdp);
    const qtyInput = qs('#pdp-qty', pdp);
    const qtyHidden = qs('#pdp-qty-hidden', pdp);

    // Product data embedded by Shopify
    const productData = window.__hikariProduct || null;

    // Gallery: scroll-snap track of slides — flick to browse, thumbs scroll to a slide,
    // and swiping keeps the active thumb in sync.
    const track = qs('.hikari-pdp__gallery-main', pdp);
    const slides = qsa('.hikari-pdp__slide', pdp);

    thumbs.forEach(function (thumb, idx) {
      on(thumb, 'click', function () {
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        if (track && slides[idx]) {
          track.scrollTo({ left: slides[idx].offsetLeft, behavior: 'smooth' });
        } else {
          setMainImage(mainImg, {
            src: thumb.dataset.imageSrc,
            srcset: thumb.dataset.imageSrcset,
            sizes: thumb.dataset.imageSizes,
            alt: thumb.dataset.imageAlt || ''
          });
        }
      });
    });

    if (track && slides.length > 1) {
      let galleryTick = false;
      on(track, 'scroll', function () {
        if (galleryTick) return;
        galleryTick = true;
        requestAnimationFrame(function () {
          galleryTick = false;
          const i = Math.round(track.scrollLeft / track.clientWidth);
          thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === i); });
        });
      });
    }

    // Quantity buttons
    qsa('.hikari-pdp__qty-btn', pdp).forEach(function (btn) {
      on(btn, 'click', function () {
        let val = parseInt(qtyInput.value, 10) || 1;
        if (btn.dataset.action === 'increment') val++;
        if (btn.dataset.action === 'decrement' && val > 1) val--;
        qtyInput.value = val;
        if (qtyHidden) qtyHidden.value = val;
      });
    });

    // Variant selection
    if (!productData) return;

    let selectedOptions = {};

    // Initialise from active buttons
    optionBtns.forEach(function (btn) {
      if (btn.classList.contains('is-active')) {
        const pos = btn.dataset.optionPosition;
        selectedOptions['option' + pos] = btn.dataset.optionValue;
      }
    });

    function findVariant() {
      return productData.variants.find(function (v) {
        return Object.keys(selectedOptions).every(function (key) {
          return v[key] === selectedOptions[key];
        });
      });
    }

    function updateUI(variant) {
      if (!variant) return;

      // Variant input
      if (variantInput) variantInput.value = variant.id;

      // Price
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      }
      if (comparePriceEl) {
        if (variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      // ATC button
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        atcBtn.setAttribute('aria-disabled', !variant.available);
        atcBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }

      // Variant image
      if (variant.featured_image && mainImg) {
        setMainImage(mainImg, {
          src: variant.featured_image.src,
          alt: variant.featured_image.alt || ''
        });
      }

      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    optionBtns.forEach(function (btn) {
      on(btn, 'click', function () {
        const pos = btn.dataset.optionPosition;
        const group = qsa('[data-option-position="' + pos + '"]', pdp);
        group.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        selectedOptions['option' + pos] = btn.dataset.optionValue;
        updateUI(findVariant());
      });
    });

    // ATC form — AJAX add to cart
    const pdpForm = qs('#pdp-form', pdp);
    on(pdpForm, 'submit', function (e) {
      e.preventDefault();
      const variantId = variantInput ? variantInput.value : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (!variantId) return;

      atcBtn.disabled = true;
      atcBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          atcBtn.textContent = 'Added ✓';
          updateCartCount();
          setTimeout(function () {
            atcBtn.disabled = false;
            atcBtn.textContent = 'Add to cart';
          }, 1800);
        })
        .catch(function () {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Try again';
        });
    });
  }

  /* ============================================================
     PDP SYSTEM OFFER — swap headline price on tier change
     (Selection itself works natively via name="id" radios; this
     only keeps the big price in sync. No-op if offer absent.)
  ============================================================ */

  function initSystemOffer() {
    const offer = qs('.hikari-pdp__offer');
    if (!offer) return;
    const pdp = qs('.hikari-pdp');
    const priceEl = qs('.hikari-pdp__price', pdp);
    const mainImg = qs('#pdp-main-img', pdp);
    const radios = qsa('.hikari-pdp__offer-radio', offer);
    if (!radios.length) return;

    // Remember the original main image so we can restore it if an option has none.
    const originalImage = mainImg ? {
      src: mainImg.getAttribute('src'),
      srcset: mainImg.getAttribute('srcset'),
      sizes: mainImg.getAttribute('sizes'),
      alt: mainImg.alt
    } : null;

    function sync() {
      const checked = offer.querySelector('.hikari-pdp__offer-radio:checked');
      if (!checked) return;
      // Headline price
      if (priceEl && checked.dataset.price) {
        const cents = parseInt(checked.dataset.price, 10);
        if (!isNaN(cents)) priceEl.textContent = formatMoney(cents);
      }
      // Swap the main gallery image to the chosen product (fall back to original).
      // dataset.image is a single fixed-width URL, so srcset must be cleared or the
      // browser would keep resolving against the previous image's candidates.
      if (mainImg) {
        const img = checked.dataset.image;
        if (img && img.length) setMainImage(mainImg, { src: img });
        else if (originalImage) setMainImage(mainImg, originalImage);
      }
    }

    radios.forEach(function (r) { on(r, 'change', sync); });
    sync();
  }

  /* ============================================================
     PDP PURCHASE OPTIONS (one-time vs subscribe & save)
     The subscription plan lives on a specific product (e.g. the
     Refill). When the offer selector points at a different product
     — which has no plan — hide the toggle and force one-time so a
     stray selling_plan is never POSTed against the wrong product.
  ============================================================ */

  function initPurchaseOptions() {
    var sub = qs('#pdp-sub');
    if (!sub) return;
    var offer = qs('.hikari-pdp__offer');
    var planRadio = sub.querySelector('[data-sub-role="plan"]');
    var oneTime = sub.querySelector('[data-sub-role="onetime"]');
    var planNameEl = sub.querySelector('[data-sub-plan-name]');
    var planPriceEl = sub.querySelector('[data-sub-plan-price]');
    var onetimePriceEl = sub.querySelector('[data-sub-onetime-price]');

    // Populate + reveal the toggle from a source element's data-sub-* attributes
    // (the selected offer radio, or the fieldset itself for a simple product).
    function applyFrom(src) {
      var planId = (src && src.dataset) ? src.dataset.subPlan : '';
      if (planId) {
        sub.hidden = false;
        if (planRadio) planRadio.value = planId;
        if (planNameEl) planNameEl.textContent = src.dataset.subName || 'Subscribe & save';
        if (planPriceEl && src.dataset.subPrice) planPriceEl.textContent = formatMoney(parseInt(src.dataset.subPrice, 10));
        if (onetimePriceEl && src.dataset.price) onetimePriceEl.textContent = formatMoney(parseInt(src.dataset.price, 10));
      } else {
        sub.hidden = true;
        if (planRadio) planRadio.value = '';
        if (oneTime) oneTime.checked = true;
      }
    }

    if (offer) {
      function sync() {
        if (oneTime) oneTime.checked = true; // reset to one-time whenever the product changes
        applyFrom(offer.querySelector('.hikari-pdp__offer-radio:checked'));
      }
      qsa('.hikari-pdp__offer-radio', offer).forEach(function (r) { on(r, 'change', sync); });
      sync();
    } else {
      applyFrom(sub); // simple product — read the fieldset's own data-sub-* attributes
    }
  }

  /* ============================================================
     PDP IN-PAGE SCENT SWITCH
     Tapping another scent swaps image, price, copy, ingredient
     bars and the buy target in place (keeping the chosen format)
     — no page reload. Falls back to the scent collection link if
     the config/data is unavailable (progressive enhancement).
  ============================================================ */

  function initScentSwitch() {
    const pdp = qs('.hikari-pdp');
    if (!pdp) return;
    const cfgEl = document.getElementById('hikari-pdp-config');
    if (!cfgEl) return;
    let config;
    try { config = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    const pills = qsa('.hikari-pdp__scent-pill', pdp);
    if (!pills.length || !config.scents) return;

    const mainImg = qs('#pdp-main-img', pdp);
    const priceEl = qs('.hikari-pdp__price', pdp);
    const compareEl = qs('.hikari-pdp__compare-price', pdp);
    const offer = qs('.hikari-pdp__offer', pdp);
    const offerRadios = qsa('.hikari-pdp__offer-radio', pdp);

    function curFormat() {
      if (offer) {
        const ck = offer.querySelector('.hikari-pdp__offer-radio:checked');
        if (ck && ck.dataset.format) return ck.dataset.format;
      }
      return pdp.getAttribute('data-format') || 'diffuser';
    }
    function productFor(scent, format) {
      const list = config.products || [];
      return list.find(function (x) { return x.scent === scent && x.format === format; })
          || list.find(function (x) { return x.scent === scent && x.format === 'diffuser'; })
          || list.find(function (x) { return x.scent === scent; })
          || null;
    }
    function setText(sel, val) { const el = qs(sel, pdp); if (el && val != null) el.textContent = val; }
    function priceHTML(prod) {
      let html = formatMoney(prod.price);
      if (prod.compare && prod.compare > prod.price) html += ' <s class="hikari-pdp__offer-was">' + formatMoney(prod.compare) + '</s>';
      return html;
    }

    function apply(scent) {
      const s = config.scents[scent];
      if (!s) return;
      const prod = productFor(scent, curFormat());

      pdp.style.setProperty('--pdp-colour', s.colour);
      pdp.style.setProperty('--pdp-tint', s.tint);
      pdp.setAttribute('data-scent', scent);

      // Identity
      setText('.hikari-pdp__title-name', (s.name || '').toUpperCase());
      setText('.hikari-pdp__title-kanji', s.kanji);
      setText('.hikari-pdp__descriptor', (s.descriptor || '').toUpperCase());
      const fnEl = qs('.hikari-pdp__function', pdp);
      if (fnEl) { fnEl.textContent = s.function + '.'; fnEl.style.color = s.colour; }

      // Image + price + buy target + description + breadcrumb
      if (prod) {
        if (mainImg && prod.image) setMainImage(mainImg, { src: prod.image, alt: prod.title || s.name });
        if (priceEl) priceEl.innerHTML = formatMoney(prod.price);
        if (compareEl) {
          if (prod.compare && prod.compare > prod.price) { compareEl.textContent = formatMoney(prod.compare); compareEl.style.display = ''; }
          else compareEl.style.display = 'none';
        }
        const descEl = qs('.hikari-pdp__description', pdp);
        if (descEl && prod.desc != null) descEl.innerHTML = prod.desc;
        const hiddenId = pdp.querySelector('input[name="id"]:not(.hikari-pdp__offer-radio)');
        if (hiddenId) hiddenId.value = prod.id;
        setText('.hikari-breadcrumb__current', (prod.title || s.name).toUpperCase());
      }

      // "What you're working with" — the only place the notes render
      const formulaCols = qsa('.hikari-pdp__formula-col', pdp);
      (s.notes || []).forEach(function (note, i) {
        const col = formulaCols[i]; if (!col) return;
        const nm = qs('.hikari-pdp__formula-note', col); if (nm) nm.textContent = note.name;
        const fl = qs('.hikari-pdp__formula-bar-fill', col); if (fl) { fl.style.width = note.pct + '%'; fl.style.background = s.colour; }
        const de = qs('.hikari-pdp__formula-desc', col); if (de) de.textContent = note.desc;
      });

      // Bottom "What you're working with" — narrative story (ingredients in bold)
      const fStory = qs('.hikari-pdp__formula-story', pdp);
      if (fStory && s.story) fStory.innerHTML = s.story;
      const fEye = qs('.hikari-pdp__formula-eyebrow', pdp); if (fEye) fEye.textContent = (s.function || '').toUpperCase();
      const fNm = qs('.hikari-pdp__formula-image-name', pdp); if (fNm) fNm.innerHTML = (s.name || '').toUpperCase() + ' <span class="hikari-pdp__formula-image-kanji">' + (s.kanji || '') + '</span>';
      const fDe = qs('.hikari-pdp__formula-image-descriptor', pdp); if (fDe) fDe.textContent = (s.descriptor || '').toUpperCase();
      const formula = qs('.hikari-pdp__formula', pdp); if (formula) { formula.style.setProperty('--pdp-colour', s.colour); formula.style.setProperty('--pdp-tint', s.tint); }
      // Flat-lay tile stays white — the tint muddied the photograph. Only the
      // placeholder state (no image) needs the scent tint to read as a scent.
      const fWrap = qs('.hikari-pdp__formula-image', pdp);
      if (fWrap) fWrap.style.background = qs('.hikari-pdp__formula-image-asset', fWrap) ? '' : s.tint;
      const fAsset = qs('.hikari-pdp__formula-image-asset', pdp); if (fAsset && s.flatlay) fAsset.src = s.flatlay;

      // Reconfigure the format offer radios to the new scent (keep current format checked)
      if (offer) {
        offerRadios.forEach(function (radio) {
          const f = radio.dataset.format; if (!f) return;
          const op = productFor(scent, f);
          const card = radio.closest('.hikari-pdp__offer-card');
          if (op && op.format === f) {
            radio.value = op.id; radio.dataset.price = op.price; radio.dataset.image = op.image; radio.disabled = !op.available;
            if (card) card.style.display = '';
            const pr = card && card.querySelector('.hikari-pdp__offer-price'); if (pr) pr.innerHTML = priceHTML(op);
            if (f === 'system') { const nm = card && card.querySelector('.hikari-pdp__offer-name'); if (nm) nm.textContent = 'The complete ' + s.name + ' System'; }
          } else {
            radio.disabled = true; if (card) card.style.display = 'none';
          }
        });
        let ck = offer.querySelector('.hikari-pdp__offer-radio:checked');
        if (!ck || ck.disabled) {
          const firstOk = offerRadios.filter(function (r) { return !r.disabled; })[0];
          if (firstOk) firstOk.checked = true;
          ck = offer.querySelector('.hikari-pdp__offer-radio:checked');
        }
        if (ck) {
          if (priceEl && ck.dataset.price) priceEl.innerHTML = formatMoney(parseInt(ck.dataset.price, 10));
          if (mainImg && ck.dataset.image) setMainImage(mainImg, { src: ck.dataset.image });
        }
      }

      // The config carries one image per product, so the original thumbnails are now stale
      const thumbs = qs('.hikari-pdp__thumbs', pdp); if (thumbs) thumbs.style.display = 'none';

      // Active pill state
      pills.forEach(function (p) { p.classList.toggle('is-active', p.dataset.scent === scent); });

      // Add-to-cart availability
      const atc = qs('.hikari-pdp__atc', pdp);
      if (atc) {
        let avail;
        if (offer) { const c = offer.querySelector('.hikari-pdp__offer-radio:checked'); avail = c ? !c.disabled : true; }
        else { avail = prod ? prod.available : true; }
        atc.disabled = !avail; atc.textContent = avail ? 'ADD TO CART' : 'SOLD OUT';
      }
    }

    pills.forEach(function (pill) {
      on(pill, 'click', function (e) {
        if (!config.scents[pill.dataset.scent]) return; // no data → allow the link to navigate
        e.preventDefault();
        apply(pill.dataset.scent);
      });
    });
  }

  function formatMoney(cents) {
    return (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents)
      : '£' + (cents / 100).toFixed(2);
  }

  // Free-shipping progress bar on the cart page — recomputes on AJAX qty changes.
  function updateFreeShipBar(total) {
    var bar = qs('#cart-freeship');
    if (!bar) return;
    var threshold = parseInt(bar.dataset.threshold, 10) || 0;
    if (!threshold) return;
    var fill = qs('.hikari-cart__freeship-fill', bar);
    var msg = qs('.hikari-cart__freeship-msg', bar);
    var pct = Math.min(100, Math.round((total / threshold) * 100));
    if (fill) fill.style.width = pct + '%';
    if (!msg) return;
    if (total >= threshold) {
      msg.classList.add('hikari-cart__freeship-msg--done');
      msg.innerHTML = 'You’ve unlocked <strong>free UK delivery</strong>.';
    } else {
      msg.classList.remove('hikari-cart__freeship-msg--done');
      msg.innerHTML = 'You’re <strong>' + formatMoney(threshold - total) + '</strong> away from <strong>free UK delivery</strong>.';
    }
  }

  /* ============================================================
     CART: COUNT UPDATE + LINE ITEM AJAX
  ============================================================ */

  function updateCartCount() {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const countEls = qsa('.hikari-cart-count');
        countEls.forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      });
  }

  function initCart() {
    const cartForm = qs('#cart-form');
    if (!cartForm) return;

    // Quantity buttons in cart
    qsa('.hikari-cart__qty-btn').forEach(function (btn) {
      on(btn, 'click', function () {
        const key = btn.dataset.key;
        const input = qs('.hikari-cart__qty-input[data-key="' + key + '"]');
        if (!input) return;
        let val = parseInt(input.value, 10) || 1;
        if (btn.dataset.action === 'increment') val++;
        if (btn.dataset.action === 'decrement') val = Math.max(0, val - 1);
        input.value = val;
        updateCartItem(key, val);
      });
    });

    // Remove buttons
    qsa('.hikari-cart__item-remove').forEach(function (btn) {
      on(btn, 'click', function () {
        updateCartItem(btn.dataset.key, 0);
      });
    });
  }

  function updateCartItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount();
        // Update subtotal
        const subtotalEl = qs('#cart-subtotal');
        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
        // Update free-shipping progress bar
        updateFreeShipBar(cart.total_price);
        // Remove item row if quantity is 0
        if (quantity === 0) {
          const row = qs('[data-key="' + key + '"]');
          if (row) row.remove();
        }
        // Reload if cart is empty
        if (cart.item_count === 0) window.location.reload();
      });
  }

  /* ============================================================
     FAQ ACCORDION
  ============================================================ */

  function initFAQ() {
    const questions = qsa('.hikari-faq__question');

    questions.forEach(function (btn) {
      on(btn, 'click', function () {
        const answer = qs('#' + btn.getAttribute('aria-controls'));
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';

        // Close all
        questions.forEach(function (q) {
          q.setAttribute('aria-expanded', 'false');
          const a = qs('#' + q.getAttribute('aria-controls'));
          if (a) a.hidden = true;
        });

        // Open this one (unless it was already open)
        if (!isExpanded) {
          btn.setAttribute('aria-expanded', 'true');
          if (answer) answer.hidden = false;
        }
      });
    });

    // FAQ sidebar active link on scroll
    const categories = qsa('.hikari-faq__category');
    const catLinks = qsa('.hikari-faq__cat-link');

    if (categories.length && catLinks.length) {
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            catLinks.forEach(function (link) {
              link.classList.toggle('is-active', link.getAttribute('href') === '#' + id);
            });
          }
        });
      }, { rootMargin: '-30% 0px -60% 0px' });

      categories.forEach(function (cat) { observer.observe(cat); });
    }
  }

  /* ============================================================
     SCENT FINDER QUIZ — AUTO-ADVANCE
  ============================================================ */

  function initFinder() {
    const intro = qs('#finder-intro');
    const quiz = qs('#finder-quiz');
    const result = qs('#finder-result');
    const startBtn = qs('#finder-start');
    const progressBar = qs('#finder-progress');
    const stepLabel = qs('#finder-step-label');
    const restartBtn = qs('#finder-restart');

    if (!intro || !quiz || !result) return;

    const finderDataEl = qs('#hikari-finder-data');
    const finderData = finderDataEl ? JSON.parse(finderDataEl.textContent) : {};

    const TOTAL_QUESTIONS = 4;
    let scores = { mori: 0, hoshi: 0, nuri: 0, haku: 0, ma: 0 };
    let currentQ = 1;
    let advancing = false;

    on(startBtn, 'click', function () {
      intro.hidden = true;
      quiz.hidden = false;
      showQuestion(1);
    });

    function showQuestion(n) {
      qsa('.hikari-finder__question').forEach(function (q) { q.hidden = true; });
      const qEl = qs('#q' + n);
      if (qEl) {
        qEl.hidden = false;
        // Fade in
        qEl.style.opacity = '0';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            qEl.style.transition = 'opacity 0.32s ease';
            qEl.style.opacity = '1';
          });
        });
      }
      updateProgress(n);
    }

    function updateProgress(n) {
      const pct = ((n - 1) / TOTAL_QUESTIONS) * 100;
      if (progressBar) progressBar.style.width = pct + '%';
      if (stepLabel) stepLabel.textContent = 'Question ' + n + ' of ' + TOTAL_QUESTIONS;
    }

    // Option selection — auto-advance after 280ms
    document.addEventListener('click', function (e) {
      const option = e.target.closest('.hikari-finder__option');
      if (!option || advancing) return;

      const questionEl = option.closest('.hikari-finder__question');
      if (!questionEl || questionEl.hidden) return;

      // Visual selection
      qsa('.hikari-finder__option', questionEl).forEach(function (o) {
        o.classList.remove('is-selected');
      });
      option.classList.add('is-selected');

      // Add points
      const points = JSON.parse(option.dataset.points || '{}');
      Object.keys(points).forEach(function (scent) {
        scores[scent] = (scores[scent] || 0) + points[scent];
      });

      advancing = true;

      // 280ms pause → fade out → next
      setTimeout(function () {
        const qEl = option.closest('.hikari-finder__question');
        if (qEl) {
          qEl.style.transition = 'opacity 0.22s ease';
          qEl.style.opacity = '0';
        }

        setTimeout(function () {
          advancing = false;
          currentQ++;
          if (currentQ > TOTAL_QUESTIONS) {
            showResult();
          } else {
            showQuestion(currentQ);
          }
        }, 240);
      }, 280);
    });

    function hexToRgba(hex, alpha) {
      var h = (hex || '').replace('#', '');
      if (h.length !== 6) return 'rgba(42,42,42,' + alpha + ')';
      var r = parseInt(h.slice(0, 2), 16);
      var g = parseInt(h.slice(2, 4), 16);
      var b = parseInt(h.slice(4, 6), 16);
      return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
    }

    function showResult() {
      quiz.hidden = true;
      result.hidden = false;

      // Find winning scent
      const winner = Object.keys(scores).reduce(function (a, b) {
        return scores[a] >= scores[b] ? a : b;
      });

      const scentInfo = (finderData.scents && finderData.scents[winner]) || {};
      const colour = scentInfo.colour || 'var(--hikari-charcoal)';
      const tint = scentInfo.tint || 'var(--hikari-bg-subtle)';
      const colourHex = scentInfo.colourHex || '#2A2A2A';
      const kanjiChar = scentInfo.kanji ? scentInfo.kanji.split(' · ')[0] : '';

      // Apply scent colour CSS vars to result container
      result.style.setProperty('--result-scent-colour', colour);
      result.style.setProperty('--result-scent-tint', tint);
      result.dataset.scent = winner;

      // Hero section
      var heroEl = qs('#finder-result-hero');
      var heroKanji = qs('#result-hero-kanji');
      var heroDescriptor = qs('#result-hero-descriptor');
      var heroFunction = qs('#result-hero-function');
      if (heroEl) {
        heroEl.style.setProperty('--result-scent-colour', colour);
        heroEl.style.setProperty('--result-scent-tint', tint);
        heroEl.style.background = tint;
      }
      if (heroKanji) heroKanji.textContent = kanjiChar;
      if (heroDescriptor) heroDescriptor.textContent = scentInfo.descriptor || '';
      if (heroFunction) heroFunction.textContent = scentInfo.desc || '';

      // Hero heading (scent name)
      var headingScent = qs('#result-heading-scent');
      if (headingScent) headingScent.textContent = scentInfo.nameUpper || winner.toUpperCase();

      // Hero CTAs
      var ctaEl = qs('#result-cta');
      var viewAllEl = qs('#result-view-all');
      if (ctaEl) {
        ctaEl.href = scentInfo.collection ? scentInfo.collection + '?format=' + (scentInfo.format || '').toLowerCase().replace(' ', '-') : (scentInfo.collection || '#');
        ctaEl.textContent = 'Shop ' + (scentInfo.name || winner) + ' ' + (scentInfo.format || 'diffuser') + ' →';
      }
      if (viewAllEl) {
        viewAllEl.href = scentInfo.collection || '#';
        viewAllEl.textContent = 'View all ' + (scentInfo.name || winner);
      }

      // Detail card: name, kanji, descriptor, function, desc
      var nameEl = qs('#result-scent-name');
      var kanjiEl = qs('#result-scent-kanji');
      var descriptorEl = qs('#result-scent-descriptor');
      var functionEl = qs('#result-scent-function');
      var descEl = qs('#result-scent-desc');
      if (nameEl) nameEl.textContent = scentInfo.nameUpper || winner.toUpperCase();
      if (kanjiEl) kanjiEl.textContent = scentInfo.kanji || '';
      if (descriptorEl) descriptorEl.textContent = scentInfo.descriptor || '';
      if (functionEl) { functionEl.textContent = scentInfo.function || ''; functionEl.style.color = colour; }
      if (descEl) descEl.textContent = scentInfo.desc || '';

      // Ingredient bars
      var primaryNote = qs('#result-primary-note');
      var secondaryNote = qs('#result-secondary-note');
      var baseNote = qs('#result-base-note');
      var primaryFill = qs('#result-primary-fill');
      var secondaryFill = qs('#result-secondary-fill');
      var baseFill = qs('#result-base-fill');
      if (primaryNote) primaryNote.textContent = scentInfo.primary || '';
      if (secondaryNote) secondaryNote.textContent = scentInfo.secondary || '';
      if (baseNote) baseNote.textContent = scentInfo.base || '';
      [primaryFill, secondaryFill, baseFill].forEach(function (el) {
        if (el) el.style.background = colour;
      });

      // Format
      var formatName = qs('#result-format-name');
      var formatDesc = qs('#result-format-desc');
      if (formatName) formatName.textContent = scentInfo.format || 'Reed diffuser';
      if (formatDesc) formatDesc.textContent = scentInfo.formatDesc || '';

      // Image column: tint bg + accent bar + real product image (or vessel fallback)
      var imageCol = qs('#result-image-col');
      var imageInner = qs('#result-image-inner');
      var vessel = qs('#result-vessel-placeholder');
      var imageLabel = qs('#result-image-label');
      if (imageCol) {
        imageCol.style.background = tint;
        imageCol.style.setProperty('--result-img-tint', tint);
        imageCol.style.setProperty('--result-img-bar', colour);
      }
      if (imageInner) {
        var prevImg = imageInner.querySelector('.hikari-finder__result-img');
        if (prevImg) prevImg.remove();
        if (scentInfo.image) {
          if (vessel) vessel.style.display = 'none';
          var img = document.createElement('img');
          img.className = 'hikari-finder__result-img';
          img.src = scentInfo.image;
          img.alt = (scentInfo.name || winner) + ' product';
          imageInner.insertBefore(img, imageInner.firstChild);
        } else {
          if (vessel) {
            vessel.style.display = '';
            vessel.style.background = hexToRgba(colourHex, 0.22);
          }
        }
      }
      if (imageLabel) imageLabel.textContent = (scentInfo.nameUpper || winner.toUpperCase()) + ' ' + kanjiChar;

      // "Also consider" — two runner-up scents with richer cards
      var alsoGrid = qs('#finder-also-grid');
      if (alsoGrid) {
        var sorted = Object.keys(scores)
          .filter(function (s) { return s !== winner; })
          .sort(function (a, b) { return scores[b] - scores[a]; })
          .slice(0, 2);

        alsoGrid.innerHTML = sorted.map(function (slug) {
          var s = (finderData.scents && finderData.scents[slug]) || {};
          var sKanji = s.kanji ? s.kanji.split(' · ')[0] : '';
          var imageContent = s.image
            ? '<img class="hikari-finder__also-img" src="' + s.image + '" alt="' + (s.name || slug) + ' product">'
            : '<div class="hikari-finder__also-vessel" style="background:' + hexToRgba(s.colourHex || '#2A2A2A', 0.22) + ';"></div>';
          var ingredients = [s.primary, s.secondary, s.base].filter(Boolean).join(' · ');
          return '<div class="hikari-finder__also-card">' +
            '<div class="hikari-finder__also-image" style="background:' + (s.tint || '#F5F3EF') + ';">' +
              '<div style="position:absolute;left:0;top:0;bottom:0;width:3px;background:' + (s.colour || '#2A2A2A') + ';z-index:2;"></div>' +
              imageContent +
              '<div class="hikari-finder__also-kanji-bg" style="color:' + (s.colour || '#2A2A2A') + ';">' + sKanji + '</div>' +
            '</div>' +
            '<div class="hikari-finder__also-body">' +
              '<div>' +
                '<p class="hikari-finder__also-scent" style="color:' + (s.colour || '#2A2A2A') + ';">' + (s.nameUpper || slug.toUpperCase()) + ' <span style="font-family:var(--font-heading);opacity:0.5;">' + sKanji + '</span></p>' +
                '<p class="hikari-finder__also-descriptor">' + (s.descriptor || '') + '</p>' +
                '<p class="hikari-finder__also-reason">' + (s.function || '') + '</p>' +
                '<p class="hikari-finder__also-ingredients">' + ingredients + '</p>' +
              '</div>' +
              '<a href="' + (s.collection || '#') + '" class="hikari-btn hikari-btn--secondary hikari-btn--inline hikari-finder__also-btn">Shop ' + (s.name || slug) + ' →</a>' +
            '</div>' +
          '</div>';
        }).join('');
      }
    }

    on(restartBtn, 'click', function () {
      scores = { mori: 0, hoshi: 0, nuri: 0, haku: 0, ma: 0 };
      currentQ = 1;
      advancing = false;
      result.hidden = true;
      qsa('.hikari-finder__option').forEach(function (o) { o.classList.remove('is-selected'); });
      quiz.hidden = false;
      showQuestion(1);
    });
  }

  /* ============================================================
     SCENTS PAGE: STICKY SUBNAV HIGHLIGHT
  ============================================================ */

  function initScentsSubnav() {
    const subnav = qs('#scents-subnav');
    const scentLinks = qsa('.hikari-scents-page__subnav-link');
    const scentSections = qsa('.hikari-scents-page__scent');

    if (!subnav || !scentSections.length) return;

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          scentLinks.forEach(function (link) {
            link.classList.toggle('is-active', link.getAttribute('href') === '#' + id);
          });
        }
      });
    }, { rootMargin: '-25% 0px -65% 0px' });

    scentSections.forEach(function (section) { observer.observe(section); });
  }

  /* ============================================================
     SCENT CAROUSEL (homepage horizontal lookbook)
  ============================================================ */

  function initScentCarousel() {
    const track = qs('#hikari-scentscroll-track');
    if (!track) return;
    const slides = qsa('.hikari-scentscroll__slide', track);
    if (!slides.length) return;

    const tabs = qsa('.hikari-scentscroll__tab');
    const prevBtn = qs('.hikari-scentscroll__arrow[data-dir="prev"]');
    const nextBtn = qs('.hikari-scentscroll__arrow[data-dir="next"]');
    const progress = qs('#hikari-scentscroll-progress');

    let current = 0;

    function setActive(index) {
      current = index;
      tabs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
      if (progress) {
        const colour = getComputedStyle(slides[index]).getPropertyValue('--scent-colour');
        progress.style.transform = 'translateX(' + (index * 100) + '%)';
        if (colour) progress.style.background = colour.trim();
      }
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === slides.length - 1;
    }

    function scrollToIndex(index) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    }

    // Keep the active tab / progress in sync as the track is scrolled (or swiped).
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          const i = slides.indexOf(entry.target);
          if (i !== -1) setActive(i);
        }
      });
    }, { root: track, threshold: [0.55] });
    slides.forEach(function (s) { observer.observe(s); });

    tabs.forEach(function (tab, i) {
      on(tab, 'click', function () { scrollToIndex(i); });
    });
    on(prevBtn, 'click', function () { scrollToIndex(current - 1); });
    on(nextBtn, 'click', function () { scrollToIndex(current + 1); });

    setActive(0);
  }

  /* ============================================================
     HERO SCROLL INDICATOR
  ============================================================ */

  function initHeroScroll() {
    const scrollIndicator = qs('.hikari-hero__scroll');
    if (!scrollIndicator) return;

    window.addEventListener('scroll', function () {
      scrollIndicator.style.opacity = window.scrollY > 80 ? '0' : '1';
    }, { passive: true });
  }

  /* ============================================================
     COLLECTION SORT
  ============================================================ */

  function initCollectionSort() {
    const sortSelect = qs('#col-sort');
    if (!sortSelect) return;

    on(sortSelect, 'change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });

    // Pre-select from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    if (sortBy) sortSelect.value = sortBy;
  }

  /* ============================================================
     INGREDIENT BARS — ANIMATE ON SCROLL
  ============================================================ */

  function initIngredientBars() {
    // PDP ingredient bars + homepage "What you're working with" note bars
    const groups = [
      { container: '.hikari-ingredient-bars', fill: '.hikari-ingredient-bars__fill' },
      { container: '.hikari-ingredients__panel', fill: '.hikari-ingredients__fill' }
    ];

    groups.forEach(function (group) {
      const bars = qsa(group.fill);
      if (!bars.length) return;

      // Store target widths, start at 0
      bars.forEach(function (bar) {
        bar.dataset.targetWidth = bar.style.width;
        bar.style.width = '0%';
      });

      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            const container = entry.target;
            qsa(group.fill, container).forEach(function (bar, i) {
              setTimeout(function () {
                bar.style.transition = 'width 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)';
                bar.style.width = bar.dataset.targetWidth;
              }, i * 120);
            });
            observer.unobserve(container);
          }
        });
      }, { threshold: 0.3 });

      qsa(group.container).forEach(function (container) { observer.observe(container); });
    });
  }

  /* ============================================================
     COLLECTION SCENT FILTER BUTTONS
  ============================================================ */

  function initCollectionFilter() {
    const filterBtns = qsa('.hikari-collection__filter-btn');
    if (!filterBtns.length) return;

    filterBtns.forEach(function (btn) {
      on(btn, 'click', function () {
        const scent = btn.dataset.scent || 'all';
        window.location.href = scent === 'all' ? '/collections/all' : '/collections/' + scent;
      });
    });
  }

  /* ============================================================
     FOOTER ACCORDION — open on desktop, collapsed on mobile
  ============================================================ */

  function initFooterAccordion() {
    var details = qsa('.hikari-footer__accord');
    if (!details.length) return;

    function update() {
      var isDesktop = window.innerWidth >= 769;
      details.forEach(function (d) {
        if (isDesktop) {
          d.setAttribute('open', '');
          var summary = d.querySelector('.hikari-footer__accord-summary');
          if (summary) summary.style.pointerEvents = 'none';
        } else {
          // Only close if not manually opened
          if (!d.dataset.userOpened) d.removeAttribute('open');
          var summary = d.querySelector('.hikari-footer__accord-summary');
          if (summary) summary.style.pointerEvents = '';
        }
      });
    }

    // Track user-opened state on mobile
    details.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (window.innerWidth < 769) {
          d.dataset.userOpened = d.open ? '1' : '';
        }
      });
    });

    update();
    window.addEventListener('resize', update);
  }

  /* ============================================================
     HOME PRODUCT GRID — curate + randomise
     Always surface at least one diffuser/spray per scent, one Duo
     and one System, then shuffle. Runs client-side so the mix
     varies on each load. Falls back to showing everything if JS
     is unavailable.
  ============================================================ */

  function initHomeGrid() {
    var grid = qs('#home-product-grid');
    if (!grid || grid.dataset.curate !== 'true') return;

    var target = parseInt(grid.dataset.gridLimit, 10) || 8;
    var cards = qsa('.hikari-card', grid);
    if (cards.length <= target) return; // small pool — nothing to curate

    var scents = ['mori', 'hoshi', 'nuri', 'haku', 'ma'];

    function shuffle(arr) {
      for (var i = arr.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
      }
      return arr;
    }
    function pick(pool) {
      return pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
    }
    function take(card) {
      if (card && chosen.indexOf(card) === -1) chosen.push(card);
    }
    function unused(filterFn) {
      return cards.filter(function (c) {
        return chosen.indexOf(c) === -1 && filterFn(c);
      });
    }

    var chosen = [];

    // 1. One diffuser OR spray for each scent (scent order randomised)
    shuffle(scents.slice()).forEach(function (s) {
      take(pick(unused(function (c) {
        return c.dataset.scent === s &&
          (c.dataset.format === 'diffuser' || c.dataset.format === 'spray');
      })));
    });

    // 2. One Duo, 3. one System (any scent)
    take(pick(unused(function (c) { return c.dataset.format === 'duo'; })));
    take(pick(unused(function (c) { return c.dataset.format === 'system'; })));

    // 4. Pad up to the target with random remaining products
    var remaining = shuffle(unused(function () { return true; }));
    while (chosen.length < target && remaining.length) chosen.push(remaining.shift());

    // 5. Trim to target, then shuffle the final display order
    chosen = shuffle(chosen.slice(0, target));

    // 6. Hide everything not chosen, then re-append chosen in shuffled order
    cards.forEach(function (c) { if (chosen.indexOf(c) === -1) c.style.display = 'none'; });
    chosen.forEach(function (c) { c.style.display = ''; grid.appendChild(c); });
  }

  /* ============================================================
     MOBILE STICKY ADD-TO-CART (PDP)
     Shows the fixed bottom bar once the in-page ATC button has
     scrolled out of view; hides it again when it's back on screen.
  ============================================================ */

  function initStickyATC() {
    var sticky = document.getElementById('hikari-pdp-sticky');
    var atc = document.querySelector('.hikari-pdp__atc');
    if (!sticky || !atc || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      // Only once the button has scrolled ABOVE the viewport — not while it is
      // still further down the page waiting to be reached.
      var passed = !e.isIntersecting && e.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', passed);
      sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
    }, { rootMargin: '-60px 0px 0px 0px' });
    io.observe(atc);
  }

  /* ============================================================
     INIT
  ============================================================ */

  /* ============================================================
     PDP FORMAT TOGGLE (two-step offer picker)
     Step 1 picks Diffuser vs Spray; cards carry data-group
     ("diffuser" | "spray" | "both") and the fieldset's
     data-active-format decides visibility (CSS). The initial
     state is server-rendered, so this only handles switching.
  ============================================================ */

  function initFormatToggle() {
    const offer = qs('#pdp-offer');
    if (!offer) return;
    const btns = qsa('.hikari-pdp__format-btn', offer);
    if (!btns.length) return;

    btns.forEach(function (btn) {
      on(btn, 'click', function () {
        const group = btn.dataset.formatGroup;
        offer.setAttribute('data-active-format', group);
        btns.forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        // If the checked option is now hidden, move the check to the
        // first visible option so price/image/ATC stay in sync.
        const checked = offer.querySelector('.hikari-pdp__offer-radio:checked');
        const checkedCard = checked && checked.closest('.hikari-pdp__offer-card');
        const visibleFor = function (card) {
          const g = card.getAttribute('data-group');
          return g === 'both' || g === group;
        };
        if (!checkedCard || !visibleFor(checkedCard)) {
          const firstVisible = qsa('.hikari-pdp__offer-card', offer).filter(function (c) {
            const r = c.querySelector('.hikari-pdp__offer-radio');
            return visibleFor(c) && r && !r.disabled;
          })[0];
          if (firstVisible) {
            const r = firstVisible.querySelector('.hikari-pdp__offer-radio');
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  }

  /* ============================================================
     CART DRAWER
     Slide-in AJAX cart. Opens from the header cart icon and on
     add-to-cart. Items render client-side from /cart.js; qty and
     remove go through /cart/change.js. The /cart page remains as
     the no-JS fallback.
  ============================================================ */

  function initCartDrawer() {
    const drawer = qs('#hikari-drawer');
    if (!drawer) return;

    const panel = qs('.hikari-drawer__panel', drawer);
    const itemsEl = qs('[data-drawer-items]', drawer);
    const emptyEl = qs('[data-drawer-empty]', drawer);
    const footEl = qs('[data-drawer-foot]', drawer);
    const subtotalEl = qs('[data-drawer-subtotal]', drawer);
    const countEl = qs('[data-drawer-count]', drawer);
    const fsWrap = qs('[data-drawer-freeship]', drawer);
    const fsMsg = qs('[data-drawer-freeship-msg]', drawer);
    const fsFill = qs('[data-drawer-freeship-fill]', drawer);
    const fsThreshold = fsWrap ? parseInt(fsWrap.dataset.threshold, 10) : 4995;
    let lastFocus = null;

    function imageUrl(src, width) {
      if (!src) return '';
      return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    }

    function render(cart) {
      // Header badge stays in sync everywhere
      qsa('.hikari-cart-count').forEach(function (el) {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
      if (countEl) countEl.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';

      const hasItems = cart.item_count > 0;
      if (emptyEl) emptyEl.hidden = hasItems;
      if (footEl) footEl.hidden = !hasItems;
      if (fsWrap) fsWrap.hidden = !hasItems;
      if (itemsEl) itemsEl.hidden = !hasItems;

      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      if (fsWrap && hasItems) {
        const remaining = fsThreshold - cart.total_price;
        const pct = Math.min(100, Math.round(cart.total_price * 100 / fsThreshold));
        if (remaining > 0) {
          fsMsg.innerHTML = 'You’re <strong>' + formatMoney(remaining) + '</strong> away from <strong>free UK delivery</strong>.';
          fsMsg.classList.remove('is-done');
        } else {
          fsMsg.innerHTML = 'You’ve unlocked <strong>free UK delivery</strong>.';
          fsMsg.classList.add('is-done');
        }
        fsFill.style.width = pct + '%';
      }

      if (!itemsEl) return;
      itemsEl.innerHTML = '';
      cart.items.forEach(function (item) {
        const row = document.createElement('div');
        row.className = 'hikari-drawer__item';
        row.setAttribute('data-key', item.key);

        const plan = item.selling_plan_allocation
          ? '<p class="hikari-drawer__item-plan">' + item.selling_plan_allocation.selling_plan.name + '</p>'
          : '';

        row.innerHTML =
          '<a href="' + item.url + '" class="hikari-drawer__item-media">' +
            (item.image ? '<img src="' + imageUrl(item.image, 240) + '" alt="" loading="lazy" width="88" height="88">' : '') +
          '</a>' +
          '<div class="hikari-drawer__item-info">' +
            '<a href="' + item.url + '" class="hikari-drawer__item-title">' + item.product_title + '</a>' +
            plan +
            '<div class="hikari-drawer__item-row">' +
              '<div class="hikari-drawer__item-qty">' +
                '<button type="button" class="hikari-drawer__qty-btn" data-drawer-qty="-1" aria-label="Decrease quantity">−</button>' +
                '<span class="hikari-drawer__qty-num">' + item.quantity + '</span>' +
                '<button type="button" class="hikari-drawer__qty-btn" data-drawer-qty="1" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<span class="hikari-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="hikari-drawer__item-remove" data-drawer-remove aria-label="Remove ' + item.product_title + '">' +
            '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>' +
          '</button>';

        itemsEl.appendChild(row);
      });
    }

    function refresh() {
      return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { render(cart); syncUpsell(cart); return cart; });
    }

    // ── Refill upsell — one tap, shown only when the cart holds a plain
    //    diffuser and nothing that already includes a refill. MORI-specific
    //    handles; extend when new scents launch.
    var UPSELL_HANDLE = 'mori-clarity-reed-diffuser-refill';
    var upsellEl = qs('[data-drawer-upsell]', drawer);
    var upsellProduct = null;

    function syncUpsell(cart) {
      if (!upsellEl) return;
      var handles = cart.items.map(function (i) { return i.handle; });
      var hasDiffuser = handles.some(function (h) { return h.indexOf('reed-diffuser') > -1 && h.indexOf('refill') === -1; });
      var covered = handles.some(function (h) {
        return h.indexOf('refill') > -1 || h.indexOf('duo') > -1 || h.indexOf('system') > -1;
      });
      if (!hasDiffuser || covered) { upsellEl.hidden = true; return; }

      var ready = upsellProduct
        ? Promise.resolve(upsellProduct)
        : fetch('/products/' + UPSELL_HANDLE + '.js')
            .then(function (r) { return r.json(); })
            .then(function (p) { upsellProduct = p; return p; });

      ready.then(function (p) {
        var v = p.variants && p.variants[0];
        if (!v || !v.available) { upsellEl.hidden = true; return; }
        var priceEl = qs('[data-upsell-price]', upsellEl);
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        var mediaEl = qs('[data-upsell-image]', upsellEl);
        if (mediaEl && p.featured_image && !mediaEl.firstChild) {
          var img = document.createElement('img');
          img.src = imageUrl(p.featured_image, 160);
          img.alt = ''; img.width = 56; img.height = 56; img.loading = 'lazy';
          mediaEl.appendChild(img);
        }
        var addBtn = qs('[data-upsell-add]', upsellEl);
        if (addBtn) addBtn.dataset.variantId = v.id;
        upsellEl.hidden = false;
      }).catch(function () { upsellEl.hidden = true; });
    }

    if (upsellEl) {
      on(qs('[data-upsell-add]', upsellEl), 'click', function (e) {
        var btn = e.currentTarget;
        if (!btn.dataset.variantId) return;
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: parseInt(btn.dataset.variantId, 10), quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () { btn.disabled = false; return refresh(); })
          .catch(function () { btn.disabled = false; });
      });
    }

    function openDrawer() {
      lastFocus = document.activeElement;
      refresh().then(function () {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('drawer-open');
        const closeBtn = qs('.hikari-drawer__close', drawer);
        if (closeBtn) closeBtn.focus();
      });
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (lastFocus) lastFocus.focus();
    }

    qsa('[data-drawer-close]', drawer).forEach(function (el) { on(el, 'click', closeDrawer); });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
    if (panel) trapFocus(panel);

    // Header cart icon (and any link to /cart) opens the drawer — except on the cart page itself.
    if (!document.body.classList.contains('template-cart')) {
      qsa('a[href="/cart"], a[href^="/cart?"]').forEach(function (link) {
        if (link.closest('.hikari-drawer')) return; // "View full cart" must navigate
        on(link, 'click', function (e) {
          e.preventDefault();
          openDrawer();
        });
      });
    }

    // Qty / remove — event delegation on the items container.
    on(itemsEl, 'click', function (e) {
      const qtyBtn = e.target.closest('[data-drawer-qty]');
      const removeBtn = e.target.closest('[data-drawer-remove]');
      if (!qtyBtn && !removeBtn) return;
      const row = e.target.closest('.hikari-drawer__item');
      if (!row) return;
      const key = row.getAttribute('data-key');
      let quantity = 0;
      if (qtyBtn) {
        const num = qs('.hikari-drawer__qty-num', row);
        quantity = Math.max(0, parseInt(num.textContent, 10) + parseInt(qtyBtn.dataset.drawerQty, 10));
      }
      row.classList.add('is-busy');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(render)
        .catch(function () { row.classList.remove('is-busy'); });
    });

    // Add to cart — intercept the PDP form, add via AJAX, open the drawer.
    const pdpForm = qs('#hikari-pdp-form');
    if (pdpForm) {
      on(pdpForm, 'submit', function (e) {
        e.preventDefault();
        const atcBtns = qsa('.hikari-pdp__atc, .hikari-pdp__sticky-atc');
        atcBtns.forEach(function (b) { b.disabled = true; });

        const fd = new FormData(pdpForm);
        if (!fd.get('selling_plan')) fd.delete('selling_plan');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () {
            atcBtns.forEach(function (b) { b.disabled = false; });
            openDrawer();
          })
          .catch(function () {
            atcBtns.forEach(function (b) { b.disabled = false; });
            // AJAX failed — fall back to the native cart page flow.
            pdpForm.submit();
          });
      });
    }
  }

  /* ============================================================
     CAROUSEL NEXT ARROW — advances mobile swipe tracks one card
     per tap; fades out at the end of the track.
  ============================================================ */

  function initCarousels() {
    qsa('.hikari-carousel').forEach(function (wrap) {
      const track = qs('[data-carousel-track]', wrap);
      const hint = qs('[data-carousel-hint]', wrap);
      if (!track || !hint) return;
      // The swipe glyph is an affordance, not a control — it steps aside
      // as soon as the user starts swiping.
      on(track, 'scroll', function () {
        hint.classList.add('is-hidden');
      }, { passive: true, once: true });
    });
  }

  function init() {
    initCarousels();
    initStickyATC();
    initScrollNav();
    initDropdown();
    initMobileNav();
    initSearch();
    initScentFilter();
    initPDP();
    initSystemOffer();
    initFormatToggle();
    initCartDrawer();
    initPurchaseOptions();
    initScentSwitch();
    initCart();
    initFAQ();
    initFinder();
    initScentsSubnav();
    initScentCarousel();
    initHeroScroll();
    initCollectionSort();
    initIngredientBars();
    initCollectionFilter();
    initFooterAccordion();
    initHomeGrid();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
